import { createSelector } from 'reselect';
import { MODELS, JOB_FUNCTIONS } from '../../../constants/admin/settings';
import { filter, capitalize } from 'lodash';
import { toSelectionFormat } from '../../../lib/collection.utils';
import {
    ALL_EVENTS_PERMISSIONS_OPTION,
    ifPermission,
    PERMISSIONS,
    ROLE_ARTIST_USER,
    ROLE_NONE,
    ROLE_ORGANIZATION_USER,
    ROLE_SUPER_ORGANIZATION_USER,
} from '../../../constants/authentication/permissions';

// STATE SELECTORS
const modalFormsDependencies = state => state.admin.modalForms.dependencies;
const modalFormsData = state => state.admin.modalForms[MODELS.PERMISSION];
const contextOptions = state => state.admin.context.options;
const currentUser = state => state.authentication.user;

export const relatedToFromFunction = (roleFunction = '') => roleFunction.split('_')[0];

export const groupOptionsSelector = createSelector(
    modalFormsData,
    modalFormsDependencies,
    contextOptions,
    (data, deps, orgs) => {
        const relatedTo = relatedToFromFunction(data.function);
        switch (relatedTo) {
            case MODELS.PARTNER:
                return toSelectionFormat(deps.partners);
            case MODELS.ORGANIZATION:
                return toSelectionFormat(orgs);
            case MODELS.ARTIST:
                return toSelectionFormat(deps.artists);
            default:
                return [];
        }
    }
);

export const rolesOptionsSelector = createSelector(
    modalFormsData,
    modalFormsDependencies,
    currentUser,
    (data, deps, user) => {
        const relatedTo = relatedToFromFunction(data.function);
        const roles = filter(
            deps.roles || [],
            r =>
                (r.type === relatedTo || !r.type) &&
                r.role !== ROLE_NONE &&
                ifPermission(user, PERMISSIONS.GOD, true, user.contextRole.level >= r.level)
        ).map(r => ({ label: capitalize(r.role.replace(/_/g, ' ')), value: r.role }));
        return roles;
    }
);

export const organizationsOptionsSelector = createSelector(contextOptions, co => toSelectionFormat(co));

export const showOrganizationsSelector = createSelector(modalFormsData, data => {
    const relatedTo = relatedToFromFunction(data.function);
    return relatedTo === MODELS.ARTIST || relatedTo === MODELS.PARTNER;
});

export const hasRoleSelectedSelector = createSelector(modalFormsData, data => {
    return data.name === ROLE_ARTIST_USER || data.name === ROLE_ORGANIZATION_USER || data.name === ROLE_SUPER_ORGANIZATION_USER
})

export const isArtistUserSelector = createSelector(modalFormsData, data => {
    return data.name === ROLE_ARTIST_USER
});

export const isOrganizationUserSelector = createSelector(modalFormsData, data => {
    return data.name === ROLE_ORGANIZATION_USER || data.name === ROLE_SUPER_ORGANIZATION_USER
});

export const eventsOptionsSelector = createSelector(modalFormsDependencies, modalFormsData, (deps, data) => {
    return [ALL_EVENTS_PERMISSIONS_OPTION].concat(
        toSelectionFormat(filter(deps.events, e => e.organizationId === data.orgId))
    );
});

export const eventDisabledSelector = createSelector(modalFormsData, showOrganizationsSelector, (data, showOrgs) => {
    return (showOrgs && !data.orgId) || !showOrgs;
});

export const functionsSelector = () => {
    return JOB_FUNCTIONS.map(x => ({ label: capitalize(x), value: x }));
};

export function transformOpenendPermission(data) {
    if (data.id) {
        return {
            ...data,
            group: data.artistId || data.partnerId || data.orgId,
            eventId: data.eventId || ALL_EVENTS_PERMISSIONS_OPTION.value
        };
    } else {
        return data;
    }
}



// WEBPACK FOOTER //
// ./src/selectors/admin/models/permissions.js